/**
 * 🎁 Розыгрыши: партнёр разыгрывает дни VPN среди клиентов своего бота.
 *
 * Порядок: черновик → задания → запуск → правка. У черновика правится всё, у
 * запущенного текст, срок и число победителей (доплата с баланса или счётом,
 * уменьшение с возвратом). Приз и задания после запуска закрыты: цена приза
 * снапшотом, а задания перепроверяются на жеребьёвке и отсекли бы вступивших.
 *
 * Отмена возвращает всё, что ещё не роздано. В дедлайн воркер сам проводит
 * жеребьёвку и пишет результат участникам в бот.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api, ApiError,
  type Giveaway, type GiveawayTaskInput,
} from '@/lib/api';
import { BottomSheet } from '@/components/ui/dialog';
import { InvoicePay } from '@/components/InvoicePay';
import { Skeleton, SkeletonCard, SkeletonLine } from '@/components/ui/skeleton';
import { confirmAction, getWebApp, haptic } from '@/lib/telegram';
import { planOptionLabel } from '@/lib/period';
import { toast } from 'sonner';
import {
  AlertTriangle, Check, Gift, Info, Loader2, Megaphone, Pencil, Plus, Rocket, Trash2, Trophy, Users, X,
} from 'lucide-react';

export const Route = createFileRoute('/giveaways')({ component: Giveaways });

const STATUS_LABEL: Record<string, string> = {
  draft: 'Черновик',
  active: 'Идёт',
  drawing: 'Жеребьёвка',
  finished: 'Завершён',
  cancelled: 'Отменён',
};

// Приз, который не выдался сразу (у поставщика сбой), выдаётся повтором сам:
// партнёру важно видеть, что победитель его получит, а не что «ошибка».
const PRIZE_LABEL: Record<string, string> = {
  issued: 'приз выдан',
  pending: 'приз выдаётся',
  refunded: 'не выдан, деньги вернули',
};

function Giveaways() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['giveaways'],
    queryFn: () => api.giveaways(),
  });

  // Telegram BackButton → на главную кабинета.
  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    let registered: (() => void) | null = null;
    const timer = window.setTimeout(() => {
      tg.BackButton.show();
      const onBack = () => navigate({ to: '/dashboard' });
      tg.BackButton.onClick(onBack);
      registered = onBack;
    }, 300);
    return () => {
      window.clearTimeout(timer);
      if (registered) tg.BackButton.offClick(registered);
      tg.BackButton.hide();
    };
  }, [navigate]);

  const list = data ?? [];

  return (
    <div className="p-4 space-y-4 pb-32">
      <div className="px-1 pt-2 flex items-center gap-3">
        <h2 className="font-display text-lg font-semibold flex-1">Розыгрыши</h2>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="h-9 px-3.5 rounded-[12px] bg-primary text-primary-foreground text-[13px] font-semibold flex items-center gap-1.5 active:scale-95 transition-transform"
        >
          <Plus className="w-4 h-4" />
          Создать
        </button>
      </div>

      <div className="flex gap-2.5 text-[12px] text-muted-foreground surface-inset rounded-[14px] p-3.5 leading-relaxed">
        <Info className="w-4 h-4 shrink-0 mt-px text-primary-light" />
        <div>
          Клиенты выполняют задания, а в назначенный момент бот сам выбирает победителей
          и выдаёт им подписку.
          <div className="mt-1.5 text-dim">
            Стоимость всех призов списывается с баланса при запуске и хранится
            замороженной. Нерозданное вернётся вам автоматически.
          </div>
        </div>
      </div>

      {isPending && <GiveawaysSkeleton />}

      {!isPending && list.length === 0 && (
        <div className="surface-inset rounded-[14px] p-8 text-center space-y-2">
          <Gift className="w-8 h-8 mx-auto text-dim" />
          <div className="font-semibold text-sm">Розыгрышей пока нет</div>
          <p className="text-[12px] text-muted-foreground leading-snug">
            Разыграйте подписку за подписку на канал, за приглашённых друзей или
            просто так. Это дешевле рекламы и приводит тех, кто уже рядом.
          </p>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="mt-1 h-10 px-4 rounded-[12px] bg-primary text-primary-foreground text-[13px] font-semibold inline-flex items-center gap-1.5 active:scale-95 transition-transform"
          >
            <Plus className="w-4 h-4" />
            Создать розыгрыш
          </button>
        </div>
      )}

      {list.map((g) => (
        <GiveawayCard key={g.id} giveaway={g} />
      ))}

      <CreateSheet open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

function GiveawayCard({ giveaway: g }: { giveaway: Giveaway }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [tasksOpen, setTasksOpen] = useState(false);
  // Запуск идёт через шит с выбором «откуда платим». Раньше кнопка молча списывала
  // баланс (сначала оплаченный карман, потом выводимую выручку), а счёт предлагала
  // только при нехватке. Партнёр, который хотел оставить выручку на вывод и оплатить
  // призы отдельно, такого выбора не имел (тикет #27).
  const [launchOpen, setLaunchOpen] = useState(false);
  const [payWith, setPayWith] = useState<'balance' | 'invoice'>('balance');
  const [editOpen, setEditOpen] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['giveaways'] });

  const { data: providersData, isError: providersFailed } = useQuery({
    queryKey: ['partner-payment-providers'],
    queryFn: () => api.partnerPaymentProviders(),
    enabled: launchOpen && payWith === 'invoice',
  });
  const { data: preview, isPending: previewPending } = useQuery({
    queryKey: ['giveaway-escrow', g.id],
    queryFn: () => api.giveawayEscrowPreview(g.id),
    enabled: launchOpen,
    staleTime: 0,
  });

  // Ничего не рассылает: собирает заготовку и уводит партнёра в composer, где он
  // правит текст и жмёт «Отправить» сам. Рассылку не отозвать.
  const announce = useMutation({
    mutationFn: () => api.announceGiveaway(g.id),
    onSuccess: ({ draft_token }) => {
      haptic('success');
      void navigate({ to: '/marketing', search: { bcastDraft: draft_token } });
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(errText(e, 'Не удалось собрать рассылку'));
    },
  });

  const launch = useMutation({
    mutationFn: () => api.launchGiveaway(g.id),
    onSuccess: () => {
      haptic('success');
      // Запуск ничего никуда не постит, и партнёр ждал обратного: «запустил, а в
      // канал не ушло». Говорим это сразу и даём кнопку в composer прямо в тосте.
      toast.success('Розыгрыш запущен', {
        description: 'Клиенты сами о нём не узнают: отправьте им рассылку.',
        duration: 12_000,
        action: { label: 'Рассказать', onClick: () => announce.mutate() },
      });
      setLaunchOpen(false);
      void refresh();
    },
    onError: (e: Error) => {
      haptic('error');
      const code = (e as { body?: { error?: { code?: string } } }).body?.error?.code;
      if (code === 'app.giveaway.insufficient_balance') {
        // Баланс изменился между превью и запуском: остаёмся в шите и показываем счёт.
        setLaunchOpen(true);
        setPayWith('invoice');
        void qc.invalidateQueries({ queryKey: ['giveaway-escrow', g.id] });
        return;
      }
      toast.error(errText(e, 'Не удалось запустить'));
    },
  });

  const cancel = useMutation({
    mutationFn: () => api.cancelGiveaway(g.id),
    onSuccess: () => {
      haptic('success');
      toast.success('Отменён, деньги вернулись на баланс');
      void refresh();
    },
    onError: (e: Error) => toast.error(errText(e, 'Не удалось отменить')),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteGiveaway(g.id),
    onSuccess: () => {
      haptic('success');
      void refresh();
    },
    onError: (e: Error) => toast.error(errText(e, 'Не удалось удалить')),
  });

  // Отмена и удаление необратимы: попап называет, что пропадёт и куда денутся деньги.
  const onCancel = async () => {
    if (busy) return;
    const ok = await confirmAction(
      `Участников: ${g.entries_count}. Розыгрыш закроется без победителей, замороженные под призы деньги вернутся на баланс.`,
      { title: `Отменить «${shortTitle(g.title)}»?`, okText: 'Да, отменить' },
    );
    if (ok) cancel.mutate();
  };
  const onRemove = async () => {
    if (busy) return;
    const ok = await confirmAction('Черновик удалится насовсем, восстановить его не получится.', {
      title: `Удалить «${shortTitle(g.title)}»?`,
      okText: 'Да, удалить',
    });
    if (ok) remove.mutate();
  };

  const busy = launch.isPending || cancel.isPending || remove.isPending;
  const isDraft = g.status === 'draft';
  const isLive = g.status === 'active' || g.status === 'drawing';
  const noTasks = g.tasks.length === 0;

  return (
    <div className="surface-inset rounded-[14px] p-3.5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold text-[13.5px] truncate">{g.title}</div>
          <div className="text-[12px] text-dim mt-0.5">
            Приз: {g.prize_plan_name}
            {g.winners_count > 1 && ` × ${g.winners_count}`}
          </div>
        </div>
        <span
          className={`text-[11.5px] font-semibold px-2 py-1 rounded-full shrink-0 ${
            isLive
              ? 'bg-white/[0.07] text-primary-light'
              : g.status === 'finished'
                ? 'bg-white/[0.06] text-muted-foreground'
                : 'bg-white/[0.06] text-dim'
          }`}
        >
          {STATUS_LABEL[g.status] ?? g.status}
        </span>
      </div>

      <div className="flex items-center gap-4 text-[12px] text-dim">
        <span className="flex items-center gap-1.5">
          <Users className="w-3 h-3" />
          {g.entries_count} участн.
        </span>
        {g.winners_drawn > 0 && (
          <span className="flex items-center gap-1.5">
            <Trophy className="w-3 h-3" />
            {g.winners_drawn} победит.
          </span>
        )}
        <span>до {fmtDate(g.ends_at)}</span>
      </div>

      {g.status === 'finished' && g.winners.length > 0 && (
        <div className="space-y-1">
          <div className="text-[12px] text-dim">Победители</div>
          {g.winners.map((w) => (
            <div key={w.slot} className="flex items-center justify-between gap-2 text-[11.5px]">
              <span className="truncate select-text">{w.display}</span>
              <span
                className={`shrink-0 ${
                  w.prize_status === 'issued' ? 'text-muted-foreground' : 'text-primary-light'
                }`}
              >
                {PRIZE_LABEL[w.prize_status] ?? w.prize_status}
              </span>
            </div>
          ))}
        </div>
      )}

      {g.tasks.length > 0 && (
        <div className="space-y-1">
          {g.tasks.map((t) => (
            <div key={t.id} className="text-[11.5px] text-muted-foreground flex items-center gap-1.5">
              <Check className="w-3 h-3 text-primary-light shrink-0" />
              {t.kind === 'channel'
                ? `Подписка на ${t.channel_name || 'канал'}`
                : `Пригласить ${t.invite_count} друзей с покупкой`}
            </div>
          ))}
        </div>
      )}

      {isDraft && noTasks && (
        <div className="flex gap-2 text-[11.5px] text-dim leading-snug">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
          Заданий нет: участвовать сможет любой клиент бота в один тап.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {isDraft && (
          <>
            <button
              type="button"
              onClick={() => setTasksOpen(true)}
              disabled={busy}
              className="flex-1 bg-white/[0.04] py-2.5 rounded-[12px] text-[13px] font-semibold disabled:opacity-50 active:scale-[0.98] transition-transform"
            >
              Задания
            </button>
            <button
              type="button"
              onClick={() => {
                haptic('light');
                setPayWith('balance');
                setLaunchOpen(true);
              }}
              disabled={busy}
              className="flex-1 bg-primary text-primary-foreground py-2.5 rounded-[12px] text-[13px] font-semibold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-transform"
            >
              <Rocket className="w-4 h-4" />
              Запустить
            </button>
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              disabled={busy}
              aria-label="Изменить"
              className="w-11 h-11 rounded-[12px] surface-inset flex items-center justify-center disabled:opacity-50 active:scale-95 transition-transform"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => void onRemove()}
              disabled={busy}
              aria-label="Удалить черновик"
              className="w-11 h-11 rounded-[12px] surface-inset flex items-center justify-center text-danger disabled:opacity-50 active:scale-95 transition-transform"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </>
        )}
        {isLive && (
          <button
            type="button"
            onClick={() => announce.mutate()}
            disabled={announce.isPending}
            className="basis-full bg-primary text-primary-foreground h-11 rounded-[12px] text-[14px] font-semibold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-transform"
          >
            {announce.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Megaphone className="w-4 h-4" />
            )}
            Рассказать клиентам
          </button>
        )}
        {isLive && (
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            disabled={busy || g.status === 'drawing'}
            aria-label="Изменить"
            title={g.status === 'drawing' ? 'Идёт жеребьёвка, подождите пару минут' : undefined}
            className="w-11 h-11 rounded-[12px] surface-inset flex items-center justify-center disabled:opacity-40 active:scale-95 transition-transform"
          >
            <Pencil className="w-4 h-4" />
          </button>
        )}
        {isLive && (
          <button
            type="button"
            onClick={() => void onCancel()}
            disabled={busy || g.status === 'drawing'}
            title={g.status === 'drawing' ? 'Идёт жеребьёвка, подождите пару минут' : undefined}
            className="flex-1 h-11 bg-white/[0.04] rounded-[12px] text-[13.5px] font-semibold text-danger disabled:opacity-40 active:scale-[0.98] transition-transform"
          >
            {cancel.isPending ? 'Отменяем…' : 'Отменить розыгрыш'}
          </button>
        )}
      </div>
      {isLive && (
        <p className="mt-2 text-[12px] leading-snug text-dim">
          Анонс не уходит сам: «Рассказать клиентам» откроет рассылку с готовым
          текстом и кнопкой участия, вы её проверите и отправите.
        </p>
      )}

      <TasksSheet
        open={tasksOpen}
        onClose={() => setTasksOpen(false)}
        giveaway={g}
      />

      <EditSheet open={editOpen} onClose={() => setEditOpen(false)} giveaway={g} />

      <BottomSheet
        open={launchOpen}
        onClose={() => setLaunchOpen(false)}
        title="Запустить розыгрыш"
      >
        <div className="space-y-4 pb-2">
          {previewPending || !preview ? (
            <div className="space-y-2">
              <SkeletonLine className="w-2/3" />
              <SkeletonLine size="xs" className="w-1/2" />
            </div>
          ) : (
            <div className="surface-inset rounded-[13px] p-3.5 space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Призовой фонд</span>
                <span className="font-semibold">
                  {fmtMoney(preview.escrow_cost_display_minor, preview.display_currency)}
                </span>
              </div>
              <div className="text-[12px] text-dim">
                {g.winners_count} × {fmtMoney(Math.round(preview.escrow_cost_display_minor / Math.max(1, g.winners_count)), preview.display_currency)} за приз.
                Замораживается при запуске, нерозданное вернётся.
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="text-[12px] font-semibold text-muted-foreground">Откуда платим</div>
            <div className="grid grid-cols-2 gap-[9px]">
              {([
                { key: 'balance', label: 'С баланса' },
                { key: 'invoice', label: 'Оплатить счётом' },
              ] as const).map((opt) => (
                <button
                  type="button"
                  key={opt.key}
                  onClick={() => {
                    haptic('selection');
                    setPayWith(opt.key);
                  }}
                  className={`rounded-[14px] px-3 py-2.5 text-[13px] font-semibold transition-colors ${
                    payWith === opt.key
                      ? 'bg-white/[0.08] border border-white/45 text-foreground'
                      : 'surface-inset text-muted-foreground'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {payWith === 'balance' ? (
            <div className="space-y-3">
              {preview && (
                <p className={`text-[11.5px] leading-snug px-1 ${preview.enough ? 'text-dim' : 'text-amber'}`}>
                  Доступно {fmtMoney(preview.spendable_display_minor, preview.display_currency)}
                  {preview.paid_usd_minor > 0 && (
                    <>
                      {' '}(заработано {fmtUsd(preview.revenue_usd_minor)}, оплачено счётом {fmtUsd(preview.paid_usd_minor)})
                    </>
                  )}
                  . Сначала списывается оплаченный карман, потом выручка.
                  {!preview.enough && ' Этого не хватает: выберите оплату счётом.'}
                </p>
              )}
              <button
                type="button"
                onClick={() => launch.mutate()}
                disabled={busy || !preview || !preview.enough}
                className="w-full bg-primary text-primary-foreground py-3 rounded-[13px] text-[14px] font-semibold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-transform"
              >
                {launch.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Rocket className="w-4 h-4" />
                )}
                Списать и запустить
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-[11.5px] text-dim leading-snug px-1">
                Розыгрыш запустится сам, как только деньги придут.{' '}
                <span className="text-amber">Вывести эти деньги нельзя: они уходят на призы.</span>
              </p>
              <InvoicePay
                providers={providersData?.providers}
                providersFailed={providersFailed}
                label="Оплатить призы"
                buildBody={(provider) => ({
                  purpose: 'giveaway_escrow' as const,
                  provider,
                  giveaway_id: g.id,
                })}
                onPaid={() => {
                  setLaunchOpen(false);
                  void refresh();
                }}
              />
            </div>
          )}
        </div>
      </BottomSheet>
    </div>
  );
}

/** Создание черновика: бот, приз, число слотов, дедлайн. */
function CreateSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [botId, setBotId] = useState<string>('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [planUuid, setPlanUuid] = useState('');
  const [winners, setWinners] = useState(1);
  const [days, setDays] = useState(7);

  const { data: bots } = useQuery({
    queryKey: ['bots'],
    queryFn: () => api.bots(),
    enabled: open,
  });
  const { data: plansData } = useQuery({
    queryKey: ['voucher-plans'],
    queryFn: () => api.voucherPlans(),
    enabled: open,
  });
  const { data: me } = useQuery({
    queryKey: ['partner'],
    queryFn: () => api.me(),
    enabled: open,
  });

  const plans = plansData?.plans ?? [];
  const selected = plans.find((p) => p.plan_uuid === planUuid) ?? null;
  // Экономика та же, что у ваучеров: партнёр платит нашу себестоимость + наценку
  // платформы, клиент за приз не платит ничего.
  const total = selected
    ? Math.round((selected.cost_per_voucher_display_minor * winners) / 100)
    : 0;
  const currency = selected?.display_currency === 'RUB' ? '₽' : selected?.display_currency ?? '';
  // Эскроу списывается из обоих карманов партнёра, как и ваучеры. Сверяемся с их
  // суммой, иначе партнёр с депозитом упрётся в ложное «не хватает».
  const balance = Math.round(me?.spendableRub ?? 0);
  const insufficient = !!selected && total > balance;

  // Остановленный бот клиентам не отвечает — розыгрыш в нём никто не увидит.
  const activeBots = useMemo(
    () => (bots ?? []).filter((b) => b.status === 'active'),
    [bots],
  );

  const create = useMutation({
    mutationFn: () =>
      api.createGiveaway({
        bot_id: botId,
        title: title.trim(),
        description: description.trim() || null,
        ends_at: new Date(Date.now() + days * 86400_000).toISOString(),
        winners_count: winners,
        prize_plan_uuid: planUuid,
      }),
    onSuccess: () => {
      haptic('success');
      toast.success('Черновик создан, добавьте задания');
      void qc.invalidateQueries({ queryKey: ['giveaways'] });
      setTitle('');
      setDescription('');
      onClose();
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(errText(e, 'Не удалось создать'));
    },
  });

  const canCreate = !!botId && !!planUuid && title.trim().length > 0 && !create.isPending;

  return (
    <BottomSheet open={open} onClose={onClose} title="Новый розыгрыш">
      <div className="space-y-4 pb-4">
        <Field label="Бот">
          <select
            value={botId}
            onChange={(e) => setBotId(e.target.value)}
            className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none"
          >
            <option value="">Выберите бота</option>
            {activeBots.map((b) => (
              <option key={b.id} value={b.id}>
                @{b.username}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Название" hint="Видно клиентам в мини-аппе.">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={128}
            placeholder="Разыгрываем месяц VPN"
            className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none"
          />
        </Field>

        <Field label="Описание" hint="Необязательно.">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            rows={3}
            className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none resize-none"
          />
        </Field>

        <Field label="Приз">
          <select
            value={planUuid}
            onChange={(e) => setPlanUuid(e.target.value)}
            className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none"
          >
            <option value="">Выберите тариф</option>
            {plans.map((p) => (
              <option key={p.plan_uuid} value={p.plan_uuid}>
                {planOptionLabel(p)}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Победителей">
            <input
              type="number"
              min={1}
              max={100}
              value={winners}
              onChange={(e) => setWinners(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
              className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none"
            />
          </Field>
          <Field label="Длительность, дней">
            <input
              type="number"
              min={1}
              max={90}
              value={days}
              onChange={(e) => setDays(Math.max(1, Math.min(90, Number(e.target.value) || 1)))}
              className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none"
            />
          </Field>
        </div>

        {selected && (
          <div
            className={`rounded-[13px] p-3.5 text-[12.5px] leading-snug ${
              insufficient
                ? 'bg-danger/[0.12] border border-danger/25 text-danger'
                : 'surface-inset text-muted-foreground'
            }`}
          >
            Спишется при запуске: <b className="text-foreground">{total} {currency}</b> за{' '}
            {winners} {plural(winners, 'приз', 'приза', 'призов')}.
            <div className="mt-1 text-dim">Доступно {balance} {currency}.</div>
            {insufficient && <div className="mt-1 font-semibold">Не хватает денег, пополните баланс.</div>}
          </div>
        )}

        <button
          type="button"
          onClick={() => create.mutate()}
          disabled={!canCreate}
          className="w-full bg-primary text-primary-foreground py-3 rounded-[13px] text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-transform"
        >
          {create.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Создать черновик
        </button>
        <p className="text-[11.5px] text-dim leading-snug">
          Деньги спишутся только при запуске. Черновик можно править и удалить бесплатно.
        </p>
      </div>
    </BottomSheet>
  );
}

/** Правка розыгрыша. Черновик: всё. Запущенный: текст, срок, победители (тикет #32). */
function EditSheet({
  open,
  onClose,
  giveaway: g,
}: {
  open: boolean;
  onClose: () => void;
  giveaway: Giveaway;
}) {
  const qc = useQueryClient();
  const isDraft = g.status === 'draft';
  const [title, setTitle] = useState(g.title);
  const [description, setDescription] = useState(g.description ?? '');
  const [endsAt, setEndsAt] = useState(toLocalInput(g.ends_at));
  const [winners, setWinners] = useState(g.winners_count);
  const [planUuid, setPlanUuid] = useState(g.prize_plan_uuid ?? '');
  const [payOpen, setPayOpen] = useState(false);

  // Открыли заново после изменений снаружи (жеребьёвка, другая правка): поля свежие.
  useEffect(() => {
    if (!open) return;
    setTitle(g.title);
    setDescription(g.description ?? '');
    setEndsAt(toLocalInput(g.ends_at));
    setWinners(g.winners_count);
    setPlanUuid(g.prize_plan_uuid ?? '');
    setPayOpen(false);
  }, [open, g]);

  const { data: plansData } = useQuery({
    queryKey: ['voucher-plans'],
    queryFn: () => api.voucherPlans(),
    enabled: open && isDraft,
  });
  const winnersChanged = !isDraft && winners !== g.winners_count;
  const { data: preview } = useQuery({
    queryKey: ['giveaway-escrow', g.id, winners],
    queryFn: () => api.giveawayEscrowPreview(g.id, winners),
    enabled: open && winnersChanged,
    staleTime: 0,
  });
  const { data: providersData, isError: providersFailed } = useQuery({
    queryKey: ['partner-payment-providers'],
    queryFn: () => api.partnerPaymentProviders(),
    enabled: payOpen,
  });

  const save = useMutation({
    mutationFn: () =>
      api.updateGiveaway(g.id, {
        title: title.trim(),
        description: description.trim() || null,
        ends_at: fromLocalInput(endsAt),
        winners_count: winners,
        ...(isDraft && planUuid && planUuid !== g.prize_plan_uuid
          ? { prize_plan_uuid: planUuid }
          : {}),
      }),
    onSuccess: () => {
      haptic('success');
      toast.success('Сохранено');
      void qc.invalidateQueries({ queryKey: ['giveaways'] });
      onClose();
    },
    onError: (e: Error) => {
      haptic('error');
      const code = (e as { body?: { error?: { code?: string } } }).body?.error?.code;
      if (code === 'app.giveaway.insufficient_balance') {
        setPayOpen(true);
        return;
      }
      toast.error(errText(e, 'Не удалось сохранить'));
    },
  });

  const canSave = title.trim().length > 0 && !!endsAt && !save.isPending;

  return (
    <BottomSheet open={open} onClose={onClose} title={isDraft ? 'Черновик' : 'Изменить розыгрыш'}>
      <div className="space-y-4 pb-4">
        <Field label="Название">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={128}
            className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none"
          />
        </Field>

        <Field label="Описание" hint="Необязательно.">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            rows={3}
            className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none resize-none"
          />
        </Field>

        {isDraft ? (
          <Field label="Приз">
            <select
              value={planUuid}
              onChange={(e) => setPlanUuid(e.target.value)}
              className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none"
            >
              {(plansData?.plans ?? []).map((p) => (
                <option key={p.plan_uuid} value={p.plan_uuid}>
                  {planOptionLabel(p)}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <div className="flex gap-2 text-[11.5px] text-dim leading-snug">
            <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
            Приз ({g.prize_plan_name}) и задания после запуска не меняются.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Победителей">
            <input
              type="number"
              min={1}
              max={100}
              value={winners}
              onChange={(e) => setWinners(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
              className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none"
            />
          </Field>
          <Field label="Окончание">
            <input
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              className="w-full surface-inset rounded-[13px] px-3 py-3 text-sm focus:outline-none"
            />
          </Field>
        </div>

        {winnersChanged && preview && (
          <div
            className={`rounded-[13px] p-3.5 text-[12.5px] leading-snug ${
              preview.escrow_cost_usd_minor > 0 && !preview.enough
                ? 'bg-danger/[0.12] border border-danger/25 text-danger'
                : 'surface-inset text-muted-foreground'
            }`}
          >
            {preview.refund_usd_minor > 0 ? (
              <>Вернётся на баланс: <b className="text-foreground">{fmtUsd(preview.refund_usd_minor)}</b></>
            ) : (
              <>
                Дополнительно спишется:{' '}
                <b className="text-foreground">{fmtMoney(preview.escrow_cost_display_minor, preview.display_currency)}</b>
                <div className="mt-1 text-dim">
                  Доступно {fmtMoney(preview.spendable_display_minor, preview.display_currency)}.
                  {!preview.enough && ' Не хватает: после сохранения предложим оплатить счётом.'}
                </div>
              </>
            )}
          </div>
        )}

        {payOpen ? (
          <div className="space-y-3">
            <p className="text-[11.5px] text-dim leading-snug px-1">
              На балансе не хватает на новых победителей. Оплатите разницу счётом, число
              победителей обновится, как только деньги придут.
            </p>
            <InvoicePay
              providers={providersData?.providers}
              providersFailed={providersFailed}
              label="Оплатить разницу"
              buildBody={(provider) => ({
                purpose: 'giveaway_escrow' as const,
                provider,
                giveaway_id: g.id,
                winners_count: winners,
              })}
              onPaid={() => {
                void qc.invalidateQueries({ queryKey: ['giveaways'] });
                onClose();
              }}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={!canSave}
            className="w-full bg-primary text-primary-foreground py-3 rounded-[13px] text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-transform"
          >
            {save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Сохранить
          </button>
        )}
      </div>
    </BottomSheet>
  );
}

/** ISO → значение для `datetime-local` в локальном времени партнёра. */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(value: string): string {
  return new Date(value).toISOString();
}

/** Задания: подписка на канал и приглашения. Канал проверяет бэк — бот должен быть админом. */
function TasksSheet({
  open,
  onClose,
  giveaway,
}: {
  open: boolean;
  onClose: () => void;
  giveaway: Giveaway;
}) {
  const qc = useQueryClient();
  const [tasks, setTasks] = useState<GiveawayTaskInput[]>(() =>
    giveaway.tasks.map((t) =>
      t.kind === 'channel'
        ? { kind: 'channel' as const, channel: t.channel_url || String(t.channel_id ?? '') }
        : { kind: 'invite' as const, invite_count: t.invite_count ?? 1 },
    ),
  );

  const save = useMutation({
    mutationFn: () => api.setGiveawayTasks(giveaway.id, tasks),
    onSuccess: () => {
      haptic('success');
      toast.success('Задания сохранены');
      void qc.invalidateQueries({ queryKey: ['giveaways'] });
      onClose();
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(errText(e, 'Не удалось сохранить'));
    },
  });

  const update = (i: number, patch: Partial<GiveawayTaskInput>) =>
    setTasks((prev) => prev.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));

  return (
    <BottomSheet open={open} onClose={onClose} title="Задания">
      <div className="space-y-3 pb-4">
        <p className="text-[12px] text-muted-foreground leading-snug">
          Участник должен выполнить все задания. Перед выдачей приза мы проверим их заново:
          отписавшийся от канала приз не получит.
        </p>

        {tasks.map((t, i) => (
          <div key={i} className="surface-inset rounded-[13px] p-3.5 space-y-2.5">
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold text-[13px]">
                {t.kind === 'channel' ? 'Подписка на канал' : 'Пригласить друзей'}
              </div>
              <button
                type="button"
                onClick={() => setTasks((prev) => prev.filter((_, idx) => idx !== i))}
                aria-label="Убрать задание"
                className="w-7 h-7 rounded-lg flex items-center justify-center text-dim active:scale-95"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {t.kind === 'channel' ? (
              <>
                <input
                  value={t.channel ?? ''}
                  onChange={(e) => update(i, { channel: e.target.value })}
                  placeholder="@channel, ссылка t.me или ID"
                  className="w-full surface-inset rounded-[12px] px-3.5 py-2.5 text-sm focus:outline-none"
                />
                <p className="text-[12px] text-dim leading-snug">
                  Бот должен быть админом канала, иначе Telegram не даст проверить подписку.
                </p>
              </>
            ) : (
              <>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={t.invite_count ?? 1}
                  onChange={(e) =>
                    update(i, {
                      invite_count: Math.max(1, Math.min(50, Number(e.target.value) || 1)),
                    })
                  }
                  className="w-full surface-inset rounded-[12px] px-3.5 py-2.5 text-sm focus:outline-none"
                />
                <p className="text-[12px] text-dim leading-snug">
                  Считаются только те друзья, кто <b>купил подписку</b> после старта розыгрыша.
                  Иначе задание накрутят пустыми аккаунтами. Ставьте планку по своей аудитории.
                  Реферальная программа не нужна: ссылку клиент получит прямо в розыгрыше.
                </p>
              </>
            )}
          </div>
        ))}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setTasks((p) => [...p, { kind: 'channel', channel: '' }])}
            disabled={tasks.length >= 5}
            className="flex-1 bg-white/[0.04] py-2.5 rounded-[12px] text-[12.5px] font-semibold disabled:opacity-40"
          >
            + Канал
          </button>
          <button
            type="button"
            onClick={() => setTasks((p) => [...p, { kind: 'invite', invite_count: 3 }])}
            disabled={tasks.length >= 5}
            className="flex-1 bg-white/[0.04] py-2.5 rounded-[12px] text-[12.5px] font-semibold disabled:opacity-40"
          >
            + Приглашения
          </button>
        </div>

        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="w-full bg-primary text-primary-foreground py-3 rounded-[13px] text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-transform"
        >
          {save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Сохранить
        </button>
      </div>
    </BottomSheet>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="text-[12px] font-semibold text-muted-foreground">{label}</div>
      {children}
      {hint && <div className="text-[12px] text-dim leading-snug">{hint}</div>}
    </div>
  );
}

/** Текст ошибки бэка: он присылает человекочитаемое сообщение, не выдумываем своё. */
/** Название в заголовок попапа: Telegram режет заголовок длиннее 64 символов. Режем по
 *  символам, а не по UTF-16, иначе эмодзи в названии разваливается пополам. */
function shortTitle(title: string): string {
  const chars = Array.from(title);
  return chars.length > 40 ? `${chars.slice(0, 40).join('')}…` : title;
}

function errText(e: Error, fallback: string): string {
  if (e instanceof ApiError) {
    const body = e.body as
      | { message?: string; detail?: string; error?: { message?: string } }
      | null;
    const msg = body?.error?.message ?? body?.message ?? body?.detail;
    if (typeof msg === 'string' && msg) return msg;
  }
  return fallback;
}

/** Русское склонение: 1 приз, 2 приза, 5 призов, 21 приз. */
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

/** Сумма в display-валюте из минорных единиц: 12345 → «123 ₽». */
function fmtMoney(displayMinor: number, currency: string): string {
  const sym = currency === 'RUB' ? '₽' : currency;
  return `${Math.round(displayMinor / 100).toLocaleString('ru-RU')} ${sym}`;
}

/** Центы USD → «$1.23» для расшифровки карманов, где курса под рукой нет. */
function fmtUsd(usdMinor: number): string {
  return `$${(usdMinor / 100).toFixed(2)}`;
}

/** Карточка розыгрыша: название с призом, бейдж статуса, счётчики участников. */
function GiveawaysSkeleton() {
  return (
    <div className="space-y-3">
      {[0, 1].map((i) => (
        <SkeletonCard key={i} className="rounded-[14px] min-h-0 p-3.5 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1 space-y-2">
              <SkeletonLine className="w-2/5" />
              <SkeletonLine size="xs" className="w-1/2" />
            </div>
            <Skeleton className="h-[22px] w-20 rounded-full shrink-0" />
          </div>
          <div className="flex items-center gap-4">
            <SkeletonLine size="xs" className="w-24" />
            <SkeletonLine size="xs" className="w-24" />
          </div>
        </SkeletonCard>
      ))}
    </div>
  );
}
